/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ReactNode } from 'react';

import { css, jsx } from '@compiled/react';
import { FormattedMessage, type MessageDescriptor } from 'react-intl-next';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { type AnyAri } from '@atlassian/ari';
import {
	isGoogleDocument,
	type ProductType,
	type SearchResultDocumentPartial,
} from '@atlassian/dynamic-cards-search-client';

import { LastModified } from '../../../../../../../common/ui/last-modified';
import { RestrictedLozenge } from '../../../../../../../common/ui/restricted-lozenge';
import { SearchMetadata } from '../../../../../../../common/ui/search-metadata';
import { SearchResult } from '../../../../../../../common/ui/search-result';
import {
	enrichExcerpt,
	isNonPublic,
	processExcerpt,
} from '../../../../../../../common/utils/search-result-utils';
import {
	useBootstrap,
	usePrimaryProduct,
	useSelectedProduct,
} from '../../../../../../../controllers/store';

import { messages } from './messages';

const footerContainer = xcss({
	paddingTop: 'space.100',
});

const contentIconStyles = css({
	width: token('space.400'),
	height: token('space.400'),
	paddingTop: token('space.025'),
	paddingRight: token('space.025'),
	paddingBottom: token('space.025'),
	paddingLeft: token('space.025'),
	boxSizing: 'border-box',
	objectFit: 'contain',
});

const outerContainerStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	height: 'size.100',
	maxWidth: '246px', // Specified in the design
});

const parentNameStyles = xcss({
	display: 'inline-block',
	overflow: 'hidden',
	font: token('font.body'),
	color: 'color.text.subtle',
	textDecoration: 'none',
	whiteSpace: 'nowrap',
	textOverflow: 'ellipsis',
});

const typeLabelContainer = xcss({
	'::first-letter': {
		textTransform: 'capitalize',
	},
});

interface DocumentSearchResultProps {
	icon: React.ReactNode;
	integrationKey?: string;
	partial: SearchResultDocumentPartial;
	/** The position in the results list shown to the user. */
	position?: number;
	resultAri: AnyAri;
}

const GOOGLE_INTEGRATION_KEY = 'google';
const MICROSOFT_INTEGRATION_KEY = 'microsoft';

const SUBTYPE_DOCUMENT = 'document';
const SUBTYPE_PRESENTATION = 'presentation';
const SUBTYPE_SPREADSHEET = 'spreadsheet';

type DocumentSubtypeLabels = Record<string, MessageDescriptor>;
const GOOGLE_LABELS: DocumentSubtypeLabels = {
	[SUBTYPE_DOCUMENT]: messages.googleDocsLabel,
	[SUBTYPE_PRESENTATION]: messages.googleSlidesLabel,
	[SUBTYPE_SPREADSHEET]: messages.googleSheetsLabel,
};

const MICROSOFT_LABELS: DocumentSubtypeLabels = {
	[SUBTYPE_DOCUMENT]: messages.msWordLabel,
	[SUBTYPE_PRESENTATION]: messages.msPowerpointLabel,
	[SUBTYPE_SPREADSHEET]: messages.msExcelLabel,
};

const getDocumentSubtypeLabel = ({
	integrationKey,
	subtype,
}: {
	integrationKey: unknown;
	subtype?: string;
}): MessageDescriptor | undefined => {
	if (!subtype) {
		return;
	}

	let label;
	if (integrationKey === GOOGLE_INTEGRATION_KEY) {
		label = GOOGLE_LABELS[subtype];
	}
	if (integrationKey === MICROSOFT_INTEGRATION_KEY) {
		label = MICROSOFT_LABELS[subtype];
	}

	return label;
};

const getParentName = (partial: SearchResultDocumentPartial) => {
	const parentName = partial.entity?.parent?.displayName;
	if (!parentName) {
		return null;
	}

	return (
		<Inline xcss={outerContainerStyles}>
			<Inline xcss={parentNameStyles}>{parentName}</Inline>
		</Inline>
	);
};

export const DocumentSearchResult = ({
	icon,
	partial,
	position,
	resultAri,
	integrationKey,
}: DocumentSearchResultProps) => {
	const [primaryProduct] = usePrimaryProduct();
	const [selectedProduct] = useSelectedProduct();
	const [{ cloudId, isAdminHubAIEnabled }] = useBootstrap();
	const { lastModifiedDate, url, permissionLevel, title, excerpt, subtype, entity } = partial;

	const contentIcon = entity?.type?.iconUrl ? (
		<img src={entity.type.iconUrl} alt="" css={contentIconStyles} />
	) : (
		icon
	);

	const product = integrationKey as ProductType;

	const label = getDocumentSubtypeLabel({
		integrationKey,
		subtype,
	});

	const isNotPublic = isNonPublic(permissionLevel);
	const shouldShowLastModified = lastModifiedDate && integrationKey !== 'atlassian-web-crawler';
	const metadataParts: [string, ReactNode][] = [
		[
			'label',
			label ? (
				<Box xcss={typeLabelContainer}>
					<FormattedMessage {...label} />
				</Box>
			) : subtype ? (
				<Box xcss={typeLabelContainer}>{subtype?.replace(/-/g, ' ')}</Box>
			) : null,
		],
		['parentName', getParentName(partial)],
		['lastModified', shouldShowLastModified && <LastModified lastModified={lastModifiedDate} />],
	];

	return (
		<SearchResult
			id={partial.id}
			description={processExcerpt(excerpt)}
			icon={contentIcon}
			headerMetadata={
				<SearchMetadata
					product={product}
					productSelected={!!selectedProduct}
					parts={metadataParts}
				/>
			}
			footer={
				isNotPublic && (
					<Box xcss={footerContainer}>
						<RestrictedLozenge restriction={permissionLevel} />
					</Box>
				)
			}
			title={enrichExcerpt(title)}
			url={url}
			isThirdParty
			cloudId={cloudId}
			primaryProduct={primaryProduct}
			position={position}
			partial={partial}
			// AI Summaries are only supported for Google Drive now
			showAISummary={isGoogleDocument(partial) && isAdminHubAIEnabled}
		/>
	);
};
